import { useId } from 'react';
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion';

export interface TvChoppTermometroProps {
  hypeHoje:     number;
  proximoMarco: number | null;
  hypeRotulo:   string;
  /** Marco recém-cruzado: a espuma cresce e escorre pela borda por alguns segundos. */
  transbordando: { marco: number; total: number } | null;
}

// Geometria do copo (viewBox 100×300). A tulipa é o próprio medidor: a borda é a
// meta, o fundo do bojo é zero.
const RIM_Y   = 16;
const BASE_Y  = 244;
const BOJO_H  = BASE_Y - RIM_Y;
const TULIPA  = 'M20,16 L80,16 C82,92 79,150 70,206 C67,228 60,240 50,244 C40,240 33,228 30,206 C21,150 18,92 20,16 Z';

/**
 * Termômetro do chopp — borda esquerda, meio da TV. Uma tulipa graduada: enche
 * conforme hype_hoje / próximo marco; a marca da meta fica gravada na borda.
 * Sempre visível com a integração ativa (em 0 já mostra a meta).
 */
export default function TvChoppTermometro({ hypeHoje, proximoMarco, hypeRotulo, transbordando }: TvChoppTermometroProps) {
  const uid = useId().replace(/:/g, '');
  const reduzirMovimento = useReducedMotion();

  const fracao = transbordando
    ? 1
    : proximoMarco && proximoMarco > 0
      ? Math.min(1, Math.max(0, hypeHoje / proximoMarco))
      : 1;
  const nivelY  = BASE_Y - fracao * BOJO_H;          // y da superfície do chopp
  const marcoNaBorda = transbordando?.marco ?? proximoMarco;

  return (
    <div
      className="absolute left-[2vw] top-1/2 z-[9] flex -translate-y-1/2 flex-col items-center pointer-events-none select-none"
      aria-live="polite"
      aria-label={`${hypeHoje} ${hypeRotulo} hoje`}
    >
      <svg
        viewBox="0 0 100 300"
        className="h-[clamp(200px,30vh,420px)] w-auto overflow-visible"
        style={{ filter: transbordando ? 'drop-shadow(0 0 22px rgba(249,115,22,0.55))' : 'drop-shadow(0 10px 24px rgba(0,0,0,0.6))' }}
      >
        <defs>
          <clipPath id={`bojo-${uid}`}><path d={TULIPA} /></clipPath>
          <linearGradient id={`chopp-${uid}`} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%"   stopColor="#fcd34d" />
            <stop offset="55%"  stopColor="#f59e0b" />
            <stop offset="100%" stopColor="#b45309" />
          </linearGradient>
          <linearGradient id={`brilho-${uid}`} x1="0" y1="0" x2="1" y2="0">
            <stop offset="0%"   stopColor="rgba(255,255,255,0)" />
            <stop offset="45%"  stopColor="rgba(255,255,255,0.20)" />
            <stop offset="100%" stopColor="rgba(120,53,15,0.28)" />
          </linearGradient>
          <linearGradient id={`vidro-${uid}`} x1="0" y1="0" x2="1" y2="0">
            <stop offset="0%"   stopColor="rgba(255,255,255,0.34)" />
            <stop offset="14%"  stopColor="rgba(255,255,255,0.10)" />
            <stop offset="55%"  stopColor="rgba(255,255,255,0.03)" />
            <stop offset="86%"  stopColor="rgba(255,255,255,0.08)" />
            <stop offset="100%" stopColor="rgba(255,255,255,0.26)" />
          </linearGradient>
        </defs>

        {/* Luz do chopp refletida na mesa */}
        <motion.ellipse cx="50" cy="268" rx="34" ry="6" fill="#f59e0b"
          initial={false} animate={{ opacity: 0.12 + fracao * 0.3 }} transition={{ duration: 0.8 }} />
        {/* Pé e haste */}
        <rect x="41" y="244" width="18" height="16" rx="3" fill="rgba(255,255,255,0.10)" />
        <rect x="26" y="258" width="48" height="8" rx="4" fill="rgba(255,255,255,0.16)" />

        {/* Vidro vazio (fundo do bojo) */}
        <path d={TULIPA} fill="rgba(30,30,40,0.72)" />

        {/* Chopp + espuma, recortados pela tulipa */}
        <g clipPath={`url(#bojo-${uid})`}>
          <motion.rect
            x="0" width="100"
            initial={false}
            animate={{ y: nivelY, height: BASE_Y - nivelY + 8 }}
            transition={{ type: 'spring', stiffness: 70, damping: 18 }}
            fill={`url(#chopp-${uid})`}
          />
          {/* Bolhas: só transform, baratas para a GPU da TV Box */}
          {!reduzirMovimento && fracao > 0.08 && [34, 50, 64].map((cx, i) => (
            <motion.circle
              key={cx}
              cx={cx} r={i === 1 ? 2.2 : 1.5}
              fill="rgba(255,247,237,0.55)"
              initial={{ cy: BASE_Y - 6, opacity: 0 }}
              animate={{ cy: [BASE_Y - 6, nivelY + 6], opacity: [0, 0.8, 0] }}
              transition={{ duration: 3.6 + i * 0.9, repeat: Infinity, ease: 'linear', delay: i * 1.3 }}
            />
          ))}
          {/* Volume do líquido: centro mais claro, borda direita mais densa */}
          <motion.rect
            x="0" width="100"
            initial={false}
            animate={{ y: nivelY, height: BASE_Y - nivelY + 8 }}
            transition={{ type: 'spring', stiffness: 70, damping: 18 }}
            fill={`url(#brilho-${uid})`}
          />
          {/* Colarinho com topo irregular */}
          <motion.path
            d="M0,10 C12,2 22,14 34,7 C46,1 56,13 68,6 C80,0 90,12 100,8 L100,24 L0,24 Z"
            initial={false}
            animate={{ y: nivelY - 16 }}
            transition={{ type: 'spring', stiffness: 70, damping: 18 }}
            fill="#fff7ed"
          />
        </g>

        {/* Reflexo e contorno do vidro */}
        <path d={TULIPA} fill={`url(#vidro-${uid})`} />
        <path d={TULIPA} fill="none" stroke="rgba(255,255,255,0.45)" strokeWidth="1.6" />
        {/* Risco de luz na lateral esquerda */}
        <path d="M27,34 C25,90 26,140 33,196" fill="none" stroke="rgba(255,255,255,0.35)" strokeWidth="2" strokeLinecap="round" />
        {/* Graduações: 1/4, 1/2, 3/4 da meta */}
        {[0.25, 0.5, 0.75].map(f => (
          <line key={f} x1="73" x2="79" y1={BASE_Y - f * BOJO_H} y2={BASE_Y - f * BOJO_H}
            stroke="rgba(255,255,255,0.28)" strokeWidth="1.2" />
        ))}
        {/* Abertura da borda: dá volume ao copo mesmo vazio */}
        <ellipse cx="50" cy={RIM_Y} rx="30" ry="4" fill="none" stroke="rgba(255,255,255,0.35)" strokeWidth="1.2" />

        {/* Graduação: a meta gravada na borda */}
        {marcoNaBorda != null && (
          <g>
            <line x1="80" y1={RIM_Y + 1} x2="92" y2={RIM_Y + 1} stroke="rgba(255,255,255,0.55)" strokeWidth="1.5" />
            <text x="95" y={RIM_Y + 5} fill="rgba(255,255,255,0.75)" fontSize="11" fontWeight="700" style={{ fontVariantNumeric: 'tabular-nums' }}>
              {marcoNaBorda}
            </text>
          </g>
        )}

        {/* Transbordo: espuma sobe da borda e escorre */}
        <AnimatePresence>
          {transbordando && (
            <motion.g
              key={transbordando.marco}
              initial={{ opacity: 0, scaleY: 0.4 }}
              animate={{ opacity: 1, scaleY: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.6, ease: 'easeOut' }}
              style={{ transformOrigin: '50px 16px' }}
            >
              <ellipse cx="50" cy="12" rx="36" ry="12" fill="#fff7ed" />
              <ellipse cx="34" cy="6"  rx="14" ry="8"  fill="#fff7ed" />
              <ellipse cx="66" cy="7"  rx="12" ry="7"  fill="#fff7ed" />
              {!reduzirMovimento && (
                <motion.path
                  d="M24,18 C22,30 23,44 26,58 C29,46 28,30 24,18 Z"
                  fill="#fff7ed"
                  initial={{ opacity: 0, y: -8 }}
                  animate={{ opacity: [0, 1, 1, 0.6], y: [-8, 0, 6, 14] }}
                  transition={{ duration: 4, ease: 'easeIn' }}
                />
              )}
            </motion.g>
          )}
        </AnimatePresence>
      </svg>

      {/* Número atual: parte do objeto, não uma etiqueta */}
      <div className="-mt-1 text-center leading-none">
        <AnimatePresence mode="wait">
          {transbordando ? (
            <motion.p
              key="marco"
              initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -6 }}
              className="text-[clamp(24px,2.6vw,48px)] font-black tabular-nums"
              style={{ textShadow: '0 2px 12px rgba(0,0,0,0.8)' }}
            >
              <span className="bg-gradient-to-b from-orange-300 to-rose-400 bg-clip-text text-transparent">{transbordando.marco}</span>
              <span className="ml-1 text-[0.55em] font-bold text-white/90">{hypeRotulo}!</span>
            </motion.p>
          ) : (
            <motion.p
              key="contador"
              initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -6 }}
              className="text-[clamp(24px,2.6vw,48px)] font-black tabular-nums text-amber-200"
              style={{ textShadow: '0 2px 12px rgba(0,0,0,0.8)' }}
            >
              {hypeHoje}
              <span className="ml-1 text-[0.42em] font-semibold text-white/80">{hypeRotulo}</span>
            </motion.p>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}
